'use client';

import { FolderTabs, Icon, type FolderTab } from '@/components/ui';
import { BRAND } from '@/lib/const';
import { MANUFACTURED_BY_LABEL_KEYS } from '@/lib/product/manufacturedByLabelKeys';
import type { TechnicalDataSection } from '@/lib/product/types';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { type ReactNode, useMemo, useState } from 'react';
import styles from './TechnicalData.module.scss';

// Mapping from section titles to translation keys
const sectionTitleKeys: Record<string, string> = {
  Movement: 'technicalData.movement.title',
  Case: 'technicalData.case.title',
  Dimensions: 'technicalData.dimensions.title',
  Strap: 'technicalData.strap.title',
};

// Mapping from item labels to translation keys (grouped by section)
const itemLabelKeys: Record<string, string> = {
  // Movement section
  Caliber: 'technicalData.movement.caliber',
  Movement: 'technicalData.movement.movement',
  'Power reserve': 'technicalData.movement.powerReserve',
  Chronograph: 'technicalData.movement.chronograph',
  Vibration: 'technicalData.movement.vibration',
  Jewel: 'technicalData.movement.jewel',
  Calendar: 'technicalData.movement.calendar',
  ...MANUFACTURED_BY_LABEL_KEYS,
  // Case section
  'Case material': 'technicalData.case.caseMaterial',
  Caseback: 'technicalData.case.caseBack',
  'Case back': 'technicalData.case.caseBack',
  'Water resistance': 'technicalData.case.waterResistance',
  Bezel: 'technicalData.case.bezel',
  Crown: 'technicalData.case.crown',
  Crystal: 'technicalData.case.crystal',
  // Dimensions section
  'Product weight (approx.)': 'technicalData.dimensions.productWeight',
  'Watch-head weight (approx.)': 'technicalData.dimensions.watchHeadWeight',
  'Watch head weight (approx.)': 'technicalData.dimensions.watchHeadWeight',
  Diameter: 'technicalData.dimensions.diameter',
  Thickness: 'technicalData.dimensions.thickness',
  'Height (upper lug tip to lower lug tip)': 'technicalData.dimensions.height',
  'Lug width (in-between lug)': 'technicalData.dimensions.lugWidth',
  // Strap section
  'Strap material': 'technicalData.strap.strapMaterial',
  'Strap color': 'technicalData.strap.strapColor',
  'Strap type': 'technicalData.strap.strapType',
  Lug: 'technicalData.strap.lug',
  'Buckle material': 'technicalData.strap.buckleMaterial',
  'Buckle type': 'technicalData.strap.buckleType',
  'Buckle size': 'technicalData.strap.buckleSize',
};

// Gallet folder-tab grouping: each tab pulls one or more section titles from the
// input sections. Sections not referenced here fall back to extra tabs at the end.
interface TabConfig {
  id: string;
  labelKey: string;
  sectionTitles: string[];
  icon?: ReactNode;
}

const GALLET_TABS: TabConfig[] = [
  { id: 'movement', labelKey: 'technicalData.movement.title', sectionTitles: ['Movement'] },
  { id: 'case', labelKey: 'technicalData.case.title', sectionTitles: ['Case'] },
  {
    id: 'dimensions-strap',
    labelKey: 'technicalData.dimensionsAndStrap.tabLabel',
    sectionTitles: ['Dimensions', 'Strap'],
  },
];

export interface TechnicalDataProps {
  sections: TechnicalDataSection[];
  heading?: string;
  expandable?: boolean;
  showBackground?: boolean;
  className?: string;
}

/**
 * Technical data section with expandable specifications (Breitling) or
 * folder-style tabs (Gallet).
 */
export function TechnicalData({
  sections,
  heading,
  expandable = true,
  showBackground = true,
  className,
}: TechnicalDataProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const t = useTranslations('product');

  const translateSectionTitle = (title: string): string => {
    const key = sectionTitleKeys[title];
    if (key) {
      const translated = t(key);
      if (translated && translated !== key) {
        return translated;
      }
    }
    return title;
  };

  const translateItemLabel = (label: string): string => {
    const key = itemLabelKeys[label];
    if (key) {
      const translated = t(key);
      if (translated && translated !== key) {
        return translated;
      }
    }
    return label;
  };

  // ---- Gallet: folder-style tabs ----
  if (BRAND === 'gallet') {
    return (
      <GalletTechnicalData
        sections={sections}
        heading={heading ?? t('technicalData.title')}
        className={className}
        t={t}
        translateSectionTitle={translateSectionTitle}
        translateItemLabel={translateItemLabel}
      />
    );
  }

  // ---- Breitling: original expandable layout ----
  const renderColumn = (section: TechnicalDataSection) => (
    <div key={section.title} className={styles.column}>
      <h3 className={styles.sectionTitle}>{translateSectionTitle(section.title)}</h3>
      <div className={styles.items}>
        {section.items.map((item) => (
          <div key={item.label} className={styles.item}>
            <span className={styles.itemLabel}>{translateItemLabel(item.label)}</span>
            <span className={styles.itemValue}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className={clsx(styles.section, className)}>
      <div className={clsx(styles.container, showBackground && styles.withBackground)}>
        <div className={styles.header}>
          <h2 className={styles.heading}>{heading ?? t('technicalData.title')}</h2>
        </div>

        <div className={clsx(styles.gridWrapper, !isExpanded && expandable && styles.truncated)}>
          <div className={styles.grid}>{sections.map(renderColumn)}</div>

          {!isExpanded && expandable && (
            <div className={styles.fadeOverlay}>
              <div className={styles.fadeGradient} />
              <div className={styles.loadMoreWrapper}>
                <button
                  className={styles.loadMoreButton}
                  onClick={() => setIsExpanded(true)}
                  type="button"
                >
                  <span>{t('loadMore')}</span>
                  <Icon name="chevron-down" size={14} />
                </button>
              </div>
            </div>
          )}
        </div>

        {isExpanded && expandable && (
          <div className={styles.expandWrapper}>
            <button
              className={styles.expandButton}
              onClick={() => setIsExpanded(false)}
              type="button"
            >
              <span>{t('showLess')}</span>
              <Icon name="chevron-up" size={14} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

TechnicalData.displayName = 'TechnicalData';

// ----------------------------------------------------------------------------
// Gallet tabbed variant — wraps the generic FolderTabs UI with TechnicalData's
// section→tab mapping and translated section panels.
// ----------------------------------------------------------------------------

interface GalletTechnicalDataProps {
  sections: TechnicalDataSection[];
  heading: string;
  className: string | undefined;
  t: ReturnType<typeof useTranslations>;
  translateSectionTitle: (title: string) => string;
  translateItemLabel: (label: string) => string;
}

function GalletTechnicalData({
  sections,
  heading,
  className,
  t,
  translateSectionTitle,
  translateItemLabel,
}: GalletTechnicalDataProps) {
  const renderPanelSection = (section: TechnicalDataSection) => (
    <div key={section.title} className={styles.panelSection}>
      <h3 className={styles.panelSectionTitle}>{translateSectionTitle(section.title)}</h3>
      <div className={styles.panelItems}>
        {section.items.map((item) => (
          <div key={item.label} className={styles.panelItem}>
            <span className={styles.panelItemLabel}>{translateItemLabel(item.label)}</span>
            <span className={styles.panelItemValue}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // Resolve tabs against actual data: each tab keeps only sections that exist.
  // Any leftover sections become their own tab at the end so nothing is dropped.
  const tabs = useMemo<FolderTab[]>(() => {
    const byTitle = new Map(sections.map((s) => [s.title, s]));
    const used = new Set<string>();
    const resolved: FolderTab[] = [];

    for (const config of GALLET_TABS) {
      const matched = config.sectionTitles
        .map((title) => byTitle.get(title))
        .filter((s): s is TechnicalDataSection => Boolean(s));
      if (matched.length === 0) continue;
      matched.forEach((s) => used.add(s.title));
      resolved.push({
        id: config.id,
        label: t(config.labelKey),
        icon: config.icon,
        content: <div className={styles.panelGrid}>{matched.map(renderPanelSection)}</div>,
      });
    }

    for (const section of sections) {
      if (used.has(section.title)) continue;
      resolved.push({
        id: `extra-${section.title.toLowerCase().replace(/\s+/g, '-')}`,
        label: translateSectionTitle(section.title),
        content: <div className={styles.panelGrid}>{renderPanelSection(section)}</div>,
      });
    }

    return resolved;
    // renderPanelSection is stable per render; sections + translators are the
    // real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, t, translateSectionTitle, translateItemLabel]);

  if (tabs.length === 0) return null;

  return (
    <section className={clsx(styles.section, styles.galletSection, className)}>
      <h2 className={styles.visuallyHidden}>{heading}</h2>
      <FolderTabs
        tabs={tabs}
        ariaLabel={heading}
        tablistWrapClassName={styles.tablistWrap}
        panelContentClassName={styles.panelInner}
      />
    </section>
  );
}
